import { ICandidateHistory } from "../../../../App/Typings/CandidateProfile";
import { AppFeatureLookup } from "../../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../../App/State/Hooks/abacHooks";
import SortableTable from "../../../../App/Components/Table/SortableTable";
import { useSortableTableState } from "../State/CandidateHistoryTableState";
import { CandidateImportSource } from "../../../../App/Enums/CandidateEnums";
import { getChangeSourceDisplay } from "../../../../Imports/Imports";
import { CandidateStatusIcon, getStatusTooltip } from "../../../../App/Components/CandidateStatus";
import { useCandidateStatusDefinitionsQuery } from "../../../../App/Queries/CandidateStatusQueries";
import { CandidateStatusColor } from "../../../../App/Enums/CandidateStatusColor";
import { ITableColumn } from "../../../../App/Typings/TableColumn";
import { defaultCellValue } from "../../../../App/Components/Table/Subcomponents/CustomTableRow";
import { Dayjs } from "dayjs";

export default function CandidateHistoryTable({
  tableData = [],
  emptyMessage,
}: {
  tableData: ICandidateHistory[];
  emptyMessage?: string;
}) {
  const {
    getters: { rowsPerPage, page, order, orderBy },
    handlers: { handleChangePage, handleChangeRowsPerPage, handleRequestSort },
  } = useSortableTableState(tableData);

  const displayedColumns = useHistoryColumnNames();
  const { data: statusDefinitions } = useCandidateStatusDefinitionsQuery();

  const data = tableData
    ? tableData.map((c) => {
        return {
          ...flattenObject(c),
          dateChanged: c.dateChanged,
          candidateId: c.candidateId,
          changeSource: getChangeSourceDisplay(CandidateImportSource[c.changeSource]),
          leadFormId: c.leadFormId,
          lastUpdatedDate: c.changedCandidate.updatedDate,
          lastInteractedWithDate: c.changedCandidate.lastInteractedWithDate,
          statusColor: c.changedCandidate.statusColor ?? CandidateStatusColor.White,
        };
      })
    : [];

  return (
    <SortableTable
      columns={displayedColumns}
      tableData={data}
      name="candidate-history"
      emptyMessage={emptyMessage}
      useServerSidePagination={false}
      exportEnabled={false}
      customRequestSort={handleRequestSort}
      customOrder={order}
      customOrderBy={orderBy}
      defaultOrderBy={"dateChanged"}
      pagination={{
        enabled: true,
        rowsPerPage: [10, 25, 50],
        currentPage: page,
        handleChangePage: handleChangePage,
        handleChangeRowsPerPage: handleChangeRowsPerPage,
        rows: rowsPerPage,
      }}
      renderCustomCell={(
        row: { [key: string]: any },
        column: ITableColumn,
        adjustDate: (date: Date | string | Dayjs) => Dayjs,
      ) => {
        if (column.propName === "statusColor") {
          const statusColor = row.statusColor as CandidateStatusColor;
          return (
            <CandidateStatusIcon
              statusColor={statusColor}
              tooltip={getStatusTooltip(statusColor, statusDefinitions)}
            />
          );
        }
        return defaultCellValue(row, column, adjustDate);
      }}
    />
  );
}

const flattenObject = (obj: ICandidateHistory) => {
  const flattened = {} as any;

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value));
    } else {
      flattened[key] = value;
    }
  });

  return flattened;
};

const useHistoryColumnNames = () => {
  const contactInfoAccess = useFeaturePermission(AppFeatureLookup.ProfileViewCandidateContactInformation);

  return columnNames.filter((column) =>
    (column.propName as string).includes("phone") || (column.propName as string).includes("email")
      ? contactInfoAccess
      : true,
  );
};

const columnNames = [
  { label: "Date Imported", propName: "dateChanged", order: 0 },
  { label: "Source", propName: "changeSource", order: 1 },
  { label: "Status", propName: "statusColor", order: 2 },
  { label: "First Name", propName: "firstName", order: 3 },
  { label: "Last Name", propName: "lastName", order: 4 },
  { label: "Company", propName: "company", order: 5 },
  { label: "Title", propName: "title", order: 6 },
  { label: "Phone Numbers", propName: "phoneNumbers", order: 7 },
  { label: "Email Addresses", propName: "emailAddresses", order: 8 },
  { label: "Last Updated", propName: "updatedDate", order: 9 },
  { label: "Latest Interacted", propName: "lastInteractedWithDate", order: 10 },
  { label: "Tags", propName: "tags", order: 11 },
];
